import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Icon, Picker, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { ratingToColorMap, ratingToIconMap, ratingToTextMap } from "constants/rating";
import { Rating } from "interfaces/entities/ticket";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerRating, IRuleTriggerRatingChanged } from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import { changeTrigger, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerRating | IRuleTriggerRatingChanged>;
}

export const RatingTrigger: FC<IProps> = ({ input }) => {
  const equalityOptions = [createEqualityOption("is"), createEqualityOption("hasBeenAdded")];

  const selectedEqualityOptions = [createEqualityOption(input.value.type === "rating" ? "is" : "hasBeenAdded")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    // change trigger type to rating or ratingChanged depending on the selected option (is or hasBeenAdded)
    const selectedOption = selectedOptions[0];

    const alreadySelectedRatingOptions = input.value.type === "rating" ? input.value.in : input.value.newRatingIn;

    const newTriggerParams =
      selectedOption.key === "is"
        ? ({
            type: "rating",
            in: alreadySelectedRatingOptions || [],
          } as Partial<IRuleTriggerRating>)
        : ({
            type: "ratingChanged",
            newRatingIn: alreadySelectedRatingOptions || [],
          } as Partial<IRuleTriggerRatingChanged>);

    changeTrigger(input, newTriggerParams);
  };

  const ratingOptions = Object.values(Rating).map(createRatingOption);

  const selectedRatingOptions =
    input.value.type === "rating"
      ? input.value.in.map(createRatingOption)
      : input.value.newRatingIn.map(createRatingOption);

  const handleSelectRatingOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions) {
      const newRatingParams = input.value.type === "rating" ? { in: [] } : { newRatingIn: [] };
      changeTriggerValues(input, newRatingParams);
      return;
    }

    const newRatingParams =
      input.value.type === "rating"
        ? { in: selectedOptions.map(option => option.key) }
        : { newRatingIn: selectedOptions.map(option => option.key) };
    changeTriggerValues(input, newRatingParams);
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={ratingOptions}
        selected={selectedRatingOptions}
        onSelect={handleSelectRatingOption}
        searchDisabled
        placeholder="Select one rating or more..."
      />
    </>
  );
};

const RatingLabel: FC<{ rating: Rating }> = ({ rating }) => (
  <Flex gap={withVar(SpacingToken.Spacing1)} alignItems="center">
    <Icon source={ratingToIconMap[rating]} customColor={ratingToColorMap[rating]} /> {ratingToTextMap[rating]}
  </Flex>
);

const createEqualityOption = (key: "is" | "hasBeenAdded"): IPickerListItem => ({
  key,
  name: key === "is" ? "is" : "has been added",
});

const createRatingOption = (key: Rating): IPickerListItem => ({
  key,
  name: key,
  customElement: {
    listItemBody: <RatingLabel rating={key} />,
    selectedItemBody: <RatingLabel rating={key} />,
  },
});
